import { useEffect, useRef, useState } from 'react';
import { siGoogle, siDiscord } from 'simple-icons';
import { useI18n } from '../i18n';
import { useAccount } from '../account';
import { loginUrl } from '../lib/api';
import { Link, buildProfile } from '../router';
import { IconUser } from './Icons';
import { RankBadge } from './RankInsignia';

function BrandIcon({ icon }: { icon: { path: string } }) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="currentColor">
      <path d={icon.path} />
    </svg>
  );
}

// 左上角账号区:未登录=登录(Google/Discord 下拉);已登录=头像 + 昵称 + 等级。
export default function AccountChip() {
  const { t, lang } = useI18n();
  const { me, loading, signOut } = useAccount();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // 后端不可用(纯静态预览)→ 不渲染,避免出现死按钮。
  if (loading || !me || !me.uid) return null;

  const authed = me.authed;
  const a = me.avatar;
  const level = me.level ?? 1;
  const xp = me.xp ?? 0;
  const floor = me.floor ?? 0;
  const next = me.next ?? null;
  const pct = next ? Math.max(0, Math.min(100, Math.round(((xp - floor) / (next - floor)) * 100))) : 100;

  return (
    <div className="acct" ref={ref}>
      <button
        className="acct-btn"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={authed ? me.displayName || 'Account' : t.account.signIn}
      >
        {authed && a && a.startsWith('http') ? (
          <img className="acct-av" src={a} alt="" width={24} height={24} />
        ) : (
          <span className="acct-av acct-av-ph" aria-hidden="true">
            <IconUser width={15} height={15} />
          </span>
        )}
        <span className="acct-label">
          {authed ? (
            <>
              <span className="acct-name">{me.displayName || 'Explorer'}</span>
              <span className="acct-lv">
                {t.account.level} {level}
              </span>
            </>
          ) : (
            <span className="acct-name">{t.account.signIn}</span>
          )}
        </span>
      </button>

      {open && (
        <div className="acct-menu" role="menu">
          {authed ? (
            <>
              <div className="acct-rank">
                <RankBadge level={level} />
                <span className="acct-rank-lv">
                  {t.account.level} {level}
                </span>
              </div>
              <div className="acct-xpbar" aria-hidden="true">
                <i style={{ width: `${pct}%` }} />
              </div>
              <div className="acct-xptext">
                {xp} {t.xp}
                {next != null && ` · ${next} →`}
              </div>
              <div className="acct-stat">
                <span>{t.streak}</span>
                <b>{me.streak?.current ?? 0}</b>
                <span>· {t.best}</span>
                <b>{me.streak?.best ?? 0}</b>
              </div>
              <Link className="acct-action" to={buildProfile(lang, me.uid ?? '')} onClick={() => setOpen(false)}>
                {t.account.viewProfile}
              </Link>
              <button className="acct-action" onClick={() => { setOpen(false); void signOut(); }}>
                {t.account.signOut}
              </button>
            </>
          ) : (
            <>
              <p className="acct-tagline">{t.account.tagline}</p>
              <a className="acct-oauth google" href={loginUrl('google')}>
                <BrandIcon icon={siGoogle} />
                {t.account.withGoogle}
              </a>
              <a className="acct-oauth discord" href={loginUrl('discord')}>
                <BrandIcon icon={siDiscord} />
                {t.account.withDiscord}
              </a>
            </>
          )}
        </div>
      )}
    </div>
  );
}
